import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useTheme } from '@/components/theme-provider';
import { Sidebar } from '@/components/layout/sidebar';
import { KanbanBoard } from '@/components/pipeline/kanban-board';
import { PipelineTable } from '@/components/pipeline/pipeline-table';
import {
  PipelineToolbar,
  applyPipelineFilters,
  DEFAULT_PIPELINE_FILTERS,
  type PipelineView,
  type PipelineFilters,
} from '@/components/pipeline/pipeline-toolbar';
import { DealDrawer } from '@/components/deal-drawer';
import { KanbanSkeleton, PipelineTableSkeleton } from '@/components/pipeline/pipeline-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { Layers, Download } from 'lucide-react';
import { toCsv, downloadCsv, dateStamp } from '@/lib/csv';
import { usePipelineDeals, usePipelineStages, type Deal } from '@/features/pipeline/queries';
import { useUpdateDealStage } from '@/features/pipeline/mutations';
import { useUserPreferences, useUpsertPreferences } from '@/features/preferences/queries';
import { useCurrentPipeline } from '@/features/preferences/use-pipeline';

const pipelineSearchSchema = z.object({
  deal: z.string().optional(),
});

export const Route = createFileRoute('/_auth/pipeline')({
  component: PipelinePage,
  validateSearch: pipelineSearchSchema,
});

function PipelinePage(): JSX.Element {
  const { theme } = useTheme();
  const { pipeline: activePipeline } = useCurrentPipeline();
  const { data: deals, isLoading: dealsLoading } = usePipelineDeals(activePipeline);
  const { data: stages, isLoading: stagesLoading } = usePipelineStages(activePipeline);
  const { data: prefs } = useUserPreferences();
  const upsertPrefs = useUpsertPreferences();
  const updateStage = useUpdateDealStage();
  const [openDeal, setOpenDeal] = useState<Deal | null>(null);

  // Hydrate view + filters from prefs once
  const [view, setView] = useState<PipelineView>('auto');
  const [filters, setFilters] = useState<PipelineFilters>(DEFAULT_PIPELINE_FILTERS);

  useEffect(() => {
    if (prefs?.pipeline_view) setView(prefs.pipeline_view);
    if (prefs?.pipeline_filters || prefs?.pipeline_sort) {
      // Legacy prefs may have stageKeys (the old broken filter); ignore them
      // — they grouped multiple stages by name-substring. Force a clean reset.
      const f = (prefs.pipeline_filters ?? {}) as Partial<PipelineFilters>;
      const s = prefs.pipeline_sort ?? null;
      setFilters({
        stageIds: Array.isArray(f.stageIds) ? f.stageIds : [],
        owner: typeof f.owner === 'string' ? f.owner : null,
        signals: Array.isArray(f.signals) ? f.signals : [],
        search: typeof f.search === 'string' ? f.search : '',
        sortBy: s?.sortBy ?? null,
        sortDir: s?.sortDir ?? 'desc',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs?.pipeline_view, prefs?.pipeline_filters, prefs?.pipeline_sort]);

  const isLoading = dealsLoading || stagesLoading;

  const filteredDeals = useMemo(
    () => applyPipelineFilters(deals ?? [], filters),
    [deals, filters],
  );

  const effectiveView: 'kanban' | 'table' =
    view === 'auto' ? (theme === 'dense' ? 'table' : 'kanban') : view;

  const [moveError, setMoveError] = useState<string | null>(null);
  const onMoveDeal = (dealId: string, newStageId: string): void => {
    setMoveError(null);
    updateStage.mutate(
      { dealId, newStageId },
      {
        onError: (err) => {
          setMoveError(
            err instanceof Error ? err.message : 'Stage move failed — your role may not be allowed to write this stage.',
          );
        },
        onSuccess: () => setMoveError(null),
      },
    );
  };

  const onViewChange = (next: PipelineView): void => {
    setView(next);
    upsertPrefs.mutate({ pipeline_view: next });
  };

  const onExport = (): void => {
    const csv = toCsv(filteredDeals, [
      { header: 'Company', value: (d) => d.companyName },
      { header: 'Contact', value: (d) => d.contactName },
      { header: 'Vertical', value: (d) => d.vertical },
      { header: 'Stage', value: (d) => d.stageName },
      { header: 'Value', value: (d) => d.dealValue },
      { header: 'Probability %', value: (d) => d.stageProbability },
      { header: 'Days In Stage', value: (d) => d.daysInStage },
      { header: 'Owner', value: (d) => d.assignedName },
      { header: 'Status', value: (d) => d.status },
      { header: 'Turnover', value: (d) => d.turnover },
      {
        header: 'Last Contacted',
        value: (d) => (d.lastContactedAt ? new Date(d.lastContactedAt).toISOString().slice(0, 10) : ''),
      },
      {
        header: 'Updated',
        value: (d) => (d.updatedAt ? new Date(d.updatedAt).toISOString().slice(0, 10) : ''),
      },
    ]);
    downloadCsv(`pipeline-${activePipeline}-${dateStamp()}.csv`, csv);
  };

  const onFiltersChange = (next: PipelineFilters): void => {
    setFilters(next);
    // Persist filters (search/owner/signals/stageKeys) and sort separately so
    // sort-only changes don't bloat the filters payload.
    const { sortBy, sortDir, ...rest } = next;
    upsertPrefs.mutate({
      pipeline_filters: rest as never,
      pipeline_sort: { sortBy, sortDir },
    });
  };

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        <div className="pl-14 pr-6 md:px-6 py-4 border-b border-border/[0.08] flex items-center gap-3">
          <h1 className="font-display text-2xl">Pipeline</h1>
          {!isLoading && (
            <span className="text-xs text-text-mute font-mono px-2 py-0.5 rounded bg-surface-2">
              {filteredDeals.length} / {deals?.length ?? 0}
            </span>
          )}
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            disabled={isLoading || filteredDeals.length === 0}
            onClick={onExport}
            title="Export the current filtered deals to CSV"
          >
            <Download className="mr-1 h-3 w-3" />
            Export
          </Button>
        </div>
        {moveError && (
          <div className="border-b border-danger/40 bg-danger/10 px-6 py-2 text-sm text-danger flex items-center justify-between gap-3">
            <span>⚠ {moveError}</span>
            <button
              type="button"
              onClick={() => setMoveError(null)}
              className="text-xs text-danger/80 hover:text-danger"
            >
              Dismiss
            </button>
          </div>
        )}
        {!isLoading && (
          <PipelineToolbar
            view={view}
            onViewChange={onViewChange}
            filters={filters}
            onFiltersChange={onFiltersChange}
            stages={stages ?? []}
            deals={deals ?? []}
          />
        )}
        {isLoading ? (
          effectiveView === 'table' ? <PipelineTableSkeleton /> : <KanbanSkeleton />
        ) : filteredDeals.length === 0 ? (
          <EmptyState
            icon={Layers}
            title={(deals?.length ?? 0) === 0 ? 'No deals in this pipeline yet' : 'No deals match your filters'}
            description={
              (deals?.length ?? 0) === 0
                ? 'Deals appear here as leads move into the pipeline. Drag a contact onto a stage from the Contacts page to create the first one.'
                : 'Try clearing the stage, owner, or signal filters in the toolbar above.'
            }
          />
        ) : effectiveView === 'table' ? (
          <PipelineTable deals={filteredDeals} stages={stages ?? []} onOpenDeal={setOpenDeal} />
        ) : (
          <KanbanBoard
            deals={filteredDeals}
            stages={stages ?? []}
            onMoveDeal={onMoveDeal}
            onOpenDeal={setOpenDeal}
          />
        )}
      </main>
      {(() => {
        // Scope Prev/Next to the same stage as the open deal, so the drawer
        // steps through one kanban column rather than jumping across stages.
        const sameStageDeals = openDeal
          ? filteredDeals.filter((d) => d.stageId === openDeal.stageId)
          : [];
        const stageDealIds = sameStageDeals.map((d) => d.id);
        const stageCursor = openDeal
          ? sameStageDeals.findIndex((d) => d.id === openDeal.id)
          : -1;
        return (
          <DealDrawer
            deal={
              openDeal
                ? {
                    id: openDeal.id,
                    contactId: openDeal.contactId,
                    companyName: openDeal.companyName,
                    contactName: openDeal.contactName,
                    dealValue: openDeal.dealValue,
                    stageId: openDeal.stageId,
                    stageProbability: openDeal.stageProbability,
                    stageName: openDeal.stageName,
                    daysInStage: openDeal.daysInStage,
                    status: openDeal.status,
                    updatedAt: openDeal.updatedAt,
                    lastContactedAt: openDeal.lastContactedAt,
                  }
                : null
            }
            onClose={() => setOpenDeal(null)}
            dealIds={stageDealIds}
            cursor={stageCursor}
            onCursorChange={(nextCursor) => {
              const nextDeal = sameStageDeals[nextCursor];
              if (nextDeal) setOpenDeal(nextDeal);
            }}
          />
        );
      })()}
    </div>
  );
}
